<template>
  <section class="observations">
    <Sidebar
      v-if="!mapHidden && !mapMobile"
      :expanded="!mapExpanded"
      :onMobileToggle="handleMapMobileToggle"
      title="Public Observations"
      class="sidebar--lg-no-footer"
    >
      <Header slot="header" :expanded="!mapExpanded" :showObservationHeading="false">
        <div class="py-4 px-3">
          <h1 class="observation-header mb-0 font-weight-bold">Public Observations</h1>
        </div>
      </Header>

      <template slot="body">
        <div class="px-3 filters filters--secondary">
          <span class="filters__label">Filter by:</span>
          <div class="row">
            <div class="col-md-3 mb-3">
              <v-select
                class="v-select--blue vs--multiple"
                :class="{ 'vs--has-value': observationsFilters.regions && observationsFilters.regions.length }"
                placeholder="Region"
                :options="regionOptions"
                :value="observationsFilters.regions"
                @input="updateFilter('regions', $event)"
                :multiple="true"
              ></v-select>
            </div>
            <div class="col-md-3 mb-3">
              <v-select
                class="v-select--blue"
                placeholder="Observation type"
                :options="typeOptions"
                :value="observationsFilters.type"
                @input="updateFilter('type', $event)"
                :searchable="false"
              ></v-select>
            </div>
            <div class="col-md-3 mb-3">
              <v-select
                class="v-select--blue"
                placeholder="Date"
                :options="dateRangeOptions"
                :value="observationsFilters.date"
                @input="updateFilter('date', $event)"
                :clearable="false"
                :searchable="false"
              ></v-select>
              <div
                class="row mt-3 mt-lg-2"
                v-if="observationsFilters.date && observationsFilters.date.value === 'range'"
              >
                <div class="col-xl-6 mb-3 mb-lg-2">
                  <datetime
                    class="vdatetime--dark"
                    type="date"
                    inputFormat="DD/MM/YYYY"
                    :value="observationsFilters.dateFrom || ''"
                    @input="updateDate('dateFrom', $event)"
                    placeholder="Date from"
                    monday-first
                    auto-continue
                    auto-close
                  />
                </div>
                <div class="col-xl-6">
                  <datetime
                    class="vdatetime--dark"
                    type="date"
                    inputFormat="DD/MM/YYYY"
                    :value="observationsFilters.dateTo || ''"
                    @input="updateDate('dateTo', $event)"
                    placeholder="Date to"
                    monday-first
                    auto-continue
                    auto-close
                    :disabled="!observationsFilters.dateFrom"
                  />
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <v-select
                class="v-select--blue"
                placeholder="Arrange by"
                :options="sortOptions"
                :value="observationsFilters.sort"
                @input="updateFilter('sort', $event)"
                :searchable="false"
              ></v-select>
            </div>
          </div>
        </div>
        <div class="observations__results">
          <Loader v-show="isFetching" class="position-absolute" />
          <table v-show="!isFetching" class="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Region</th>
                <th>Observation type</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr class="tr--clickable" v-for="obs in observations" :key="obs.id" @click="onObservationSelect(obs.id)">
                <td class="td--40">
                  <div>
                    <span class="table__mobile-label">Description:</span>
                    {{ obs.description }}
                  </div>
                </td>
                <td>
                  <div>
                    <span class="table__mobile-label">Region:</span>
                    {{ $store.getters.regionTitleById(obs.region_id) }}
                  </div>
                </td>
                <td>
                  <div>
                    <span class="table__mobile-label">Observation type:</span>
                    {{ obs.type }}
                  </div>
                </td>
                <td>
                  <div>
                    <span class="table__mobile-label">Date:</span>
                    {{ obs.time | formatDate }}
                  </div>
                  <a
                    href="#"
                    @click.prevent="onObservationSelect(obs.id)"
                    class="button button--dark-with-light-outline table__mobile-button"
                    >See details</a
                  >
                </td>
              </tr>
              <tr v-if="!observations.length">
                <td colspan="5" class="text-center">No observations found for selected filters</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <Footer slot="footer" />
    </Sidebar>
    <Footer v-else />
  </section>
</template>

<script>
import Header from "../header/Header";
import Footer from "../footer/Footer";
import Sidebar from "../sidebar/Sidebar";
import DatesMixin from "../../mixins/dates";
import Loader from "../loader/Loader";

import mutationTypes from "../../store/mutation-types";

import { mapState, mapGetters } from "vuex";
import { Datetime } from "vue-datetime";
import moment from "moment";

export default {
  components: {
    Header,
    Footer,
    Sidebar,
    Loader,
    Datetime
  },

  mixins: [DatesMixin],

  props: {
    mapHidden: Boolean,
    mapExpanded: Boolean,
    mapMobile: Boolean,
    handleTileClick: Function,
    handleMapMobileToggle: Function,
    onObservationSelect: Function
  },

  data() {
    return {
      isFetching: false,
      observations: []
    };
  },

  computed: {
    ...mapState({
      regions: state => state.regions,
      observationsFilters: state => state.observationsFilters
    }),

    ...mapGetters(["filteredObservations"]),

    regionOptions() {
      return this.regions
        .map(r => ({
          label: r.title,
          value: r.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    typeOptions() {
      return [
        {
          label: "Avalanche",
          value: "Avalanche"
        },
        {
          label: "Snowpack",
          value: "Snowpack"
        }
      ];
    },

    dateRangeOptions() {
      return [
        {
          label: "Past 7 days",
          value: "week",
          dateFrom: moment()
            .subtract(1, "weeks")
            .format("YYYY-MM-DD"),
          isDefault: true
        },
        {
          label: "Whole season",
          value: "year",
          dateFrom: `${new Date().getFullYear()}-01-01`
        },
        {
          label: "Date range",
          value: "range"
        }
      ];
    },

    sortOptions() {
      return [
        {
          label: "Date",
          value: "time"
        },
        {
          label: "Region",
          value: "region"
        }
      ];
    }
  },

  created() {
    if (!this.observationsFilters.date) {
      const defaultOption = this.dateRangeOptions.find(o => o.isDefault);
      this.updateFilter("date", defaultOption);
    }

    if (!this.observationsFilters.sort) {
      this.observations = this.filteredObservations.sort((a, b) => b.time.localeCompare(a.time));
    }
  },

  methods: {
    updateFilter(name, option) {
      this.$store.commit(mutationTypes.UPDATE_OBSERVATIONS_FILTERS, { [name]: option });

      if (name === "date" && option.dateFrom) {
        this.$store.commit(mutationTypes.UPDATE_OBSERVATIONS_FILTERS, { dateFrom: option.dateFrom });
      }

      if (!this.observationsFilters.sort) {
        this.observations = this.filteredObservations.sort((a, b) => b.time.localeCompare(a.time));
      }
    },

    updateDate(name, date) {
      const prevDate = this.observationsFilters[name];
      const formattedDate = moment(date).format("YYYY-MM-DD");

      this.updateFilter(name, formattedDate);

      // fetch observations if needed
      const dateFrom = this.observationsFilters.dateFrom;
      const dateTo = this.observationsFilters.dateTo;

      // observations for current year are loaded by default
      let syncRequired = dateFrom < `${new Date().getFullYear()}-01-01`;

      // check for cases when data is already loaded in the store
      syncRequired =
        syncRequired && !((name == "dateFrom" && prevDate < dateFrom) || (name == "dateTo" && prevDate < dateTo));

      if (syncRequired) {
        this.isFetching = true;
        this.$store.dispatch("syncObservations", { dateFrom, dateTo }).then(() => {
          // add timeout to display the loading at least for a bit
          setTimeout(() => {
            this.isFetching = false;
          }, 500);
        });
      }
    }
  }
};
</script>
